import { useTenantAvailable } from '@api/application-configuration';
import { useLogoutMutation } from '@api/auth';
import { Bell, Globe2, Lock, Palette, PieChart, UserX } from '@tamagui/lucide-icons';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Group } from '@unistocks/components/group/group';
import { Page } from '@unistocks/components/page/page';
import { LanguageSelect } from '@unistocks/components/settings/language-select';
import { MenuItem } from '@unistocks/components/settings/menu-item';
import { PortfolioSettings } from '@unistocks/components/settings/portfolio-settings';
import { ThemeSelect } from '@unistocks/components/settings/theme-select';
import { t } from '@unistocks/localization';
import {
  useAccountDeleteAccountMutation
} from '@unistocks/proxy/axios-client/AccountQuery';
import {
  useProfileGetQuery
} from '@unistocks/proxy/axios-client/ProfileQuery';
import { useIsDarkMode } from '@unistocks/shared/hooks/use-active-color-scheme';
import { useToggle } from '@unistocks/shared/hooks/use-toggle';
import { Href, useRouter } from 'expo-router';
import { Separator, Spinner } from 'tamagui';

export const Settings: React.FC = () => {
  const isTenantUser = useTenantAvailable();
  const router = useRouter();
  const isDarkMode = useIsDarkMode();

  const changePasswordHref: Href = isTenantUser
    ? '/(tabs)/(menu)/settings/change-password'
    : '/(admin)/(menu)/settings/change-password';
  const { data: profile } = useProfileGetQuery();
  const [portfolioSettingsOpen, togglePortfolioSettingsOpen, setPortfolioSettingsOpen] =
    useToggle();

  const { mutateAsync: logoutAsync } = useLogoutMutation();
  const { mutate: deleteAccount, isPending: isLoadingDeleteAccount } =
    useAccountDeleteAccountMutation({
      onSuccess: async () => {
        await logoutAsync();
      }
    });

  return (
    <Page size="small">
      <Page.ScrollView
        headerLogo={false}
        headerTitle={t('::Menu:Settings')}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        <Page.Content p={0}>
          <Group separator={<Separator />}>
            <LanguageSelect>
              <MenuItem
                title={t('::Language')}
                subTitle={t('::Settings:Language:Description')}
                icon={<Globe2 color={'$success'} />}
              />
            </LanguageSelect>

            <ThemeSelect>
              <MenuItem
                title={t('::Settings:Theme')}
                subTitle={t('::Settings:Theme:Description')}
                icon={<Palette color={isDarkMode ? '$yellow9' : '$yellow8'} />}
              />
            </ThemeSelect>

            {isTenantUser ? (
              <MenuItem
                title={t('::Settings:Portfolio')}
                subTitle={t('::Settings:Portfolio:Description')}
                icon={<PieChart color={'$blue11'} />}
                onPress={togglePortfolioSettingsOpen}
              />
            ) : null}

            {isTenantUser ? (
              <MenuItem
                title={t('::Settings:Notifications')}
                subTitle={t('::Settings:Notifications:Description')}
                icon={<Bell color={'$info'} />}
              />
            ) : null}

            <MenuItem
              title={t('AbpAccount::ProfileTab:Password')}
              subTitle={t('AbpAccount::ProfileTab:Password')}
              icon={<Lock color={'$color'} />}
              onPress={() => router.navigate(changePasswordHref)}
            />

            {isTenantUser ? (
              <Confirm
                message={t('AbpAccount::DeleteAccount:Confirm')}
                confirmText={t('::Delete')}
                requiredInputConfirmation={
                  profile?.name
                    ? `${profile.name} ${profile.surname}`
                    : (profile?.email ?? undefined)
                }
                onConfirm={deleteAccount}
              >
                <MenuItem
                  title={t('::Settings:DeleteAccount')}
                  subTitle={t('::Settings:DeleteAccount')}
                  icon={<UserX color={'$danger'} />}
                  iconAfter={isLoadingDeleteAccount ? <Spinner color={'$unistocks'} /> : undefined}
                />
              </Confirm>
            ) : null}
          </Group>
        </Page.Content>
      </Page.ScrollView>

      {isTenantUser ? (
        <PortfolioSettings open={portfolioSettingsOpen} onOpenChange={setPortfolioSettingsOpen} />
      ) : null}
    </Page>
  );
};

Settings.displayName = 'SettingsScreen';
